import Scrollbar from 'smooth-scrollbar';
import type { ScrollbarOptions, ScrollListener } from 'smooth-scrollbar/src/interfaces/scrollbar';
import ScrollTrigger from 'gsap/dist/ScrollTrigger';
import ScrollToPlugin from 'gsap/dist/ScrollToPlugin';
import ScrollSmoother from 'gsap/dist/ScrollSmoother';
import { gsap } from 'gsap';
import { useRef, createContext, useContext, useCallback, useEffect } from 'react';
import type { ReactElement, ReactNode, RefObject } from 'react';
import classNames from 'classnames';
import type {
  ScrollStatus,
  ScrollToOptions,
  ScrollIntoViewOptions,
} from 'smooth-scrollbar/interfaces';
import styles from '@/pages/_app.module.scss';
import {
  LockScroll,
  EdgeEasingPlugin,
  ScaleDeltaPlugin,
} from 'util/scroll/smoothScrollbar.plugins';
import { isTouch } from '@/hooks/useIsDesktopDevice';
import { addEventListener } from 'seng-disposable-event-listener';
import useIsomorphicLayoutEffect from '@/hooks/useIsomorphicLayoutEffect';

gsap.registerPlugin(ScrollTrigger, ScrollToPlugin, ScrollSmoother);
// eslint-disable-next-line react-hooks/rules-of-hooks
Scrollbar.use(LockScroll, ScaleDeltaPlugin, EdgeEasingPlugin);

gsap.config({ autoSleep: 120, force3D: 'auto', autoKillThreshold: 1 });

export interface ScrollProviderContextType {
  lock(): void;

  unLock(): void;

  scaleDelta(speed?: number): void;

  resetScaleDelta(): void;

  scrollIntoView(element: Element, options?: ScrollIntoViewOptions): void;

  scrollTo(
    x: number,
    y: number,
    duration?: number,
    options?: ScrollToOptions & { target?: gsap.DOMTarget },
  ): void;

  // onScrollUpdate(callback: (status: ScrollStatus) => void): void;
  scrollStatusRef: RefObject<ScrollStatus | null>;

  scrollWrapperRef: RefObject<HTMLDivElement | null>;

  hasSmoothScroll: RefObject<boolean | null>;
  hasGsapScrollSmootherRef: RefObject<boolean | null>;
}

const ScrollProviderContext = createContext<ScrollProviderContextType | undefined>(undefined);

export function useScrollProviderContext(): ScrollProviderContextType | undefined {
  return useContext(ScrollProviderContext);
}

export interface ScrollProviderProps {
  children: ReactNode;
  wrapperClassName?: string;
  scrollBarOptions?: ScrollbarOptions;
  onScrollUpdate?: (status: ScrollStatus) => void;
  lockScroll?: boolean;
  enableSmoothScroll?: boolean;
  enableGsapScrollSmooth?: boolean;
}

let touchListener!: () => void;
let wheelListener!: () => void;
let scrollListener!: () => void;

// todo: async import specific smooth-scroll lib per devices needs.
// todo: Refactor all this code to have 3 different Classes with its own clean methods in there instead of all the if/elsing. and construct only 1 of them.
export function ScrollProvider(props: ScrollProviderProps): ReactElement {
  const scrollSmootherRef = useRef<ScrollSmoother>(undefined);
  const smoothScrollRef = useRef<Scrollbar>(undefined);
  const scrollWrapperRef = useRef<HTMLDivElement>(null);
  const scrollContentRef = useRef<HTMLDivElement>(null);
  const scrollHandlerRef = useRef<ScrollListener | ((_event: Event) => void)>(undefined);
  const scrollStatusRef = useRef<ScrollStatus | null>(null);
  const { onScrollUpdate, lockScroll } = props;
  const hasSmoothScroll = useRef<boolean>(!!props.enableSmoothScroll);
  const lockPosition = useRef<number>(undefined);
  const hasGsapScrollSmootherRef = useRef<boolean>(!!props.enableGsapScrollSmooth);

  const lock = useCallback(() => {
    if (hasGsapScrollSmootherRef.current) {
      scrollSmootherRef?.current?.paused(true);
    } else if (hasSmoothScroll.current) {
      smoothScrollRef?.current?.updatePluginOptions('lock', { isLock: true });
    } else {
      lockPosition.current = window.scrollY || document.documentElement.scrollTop;
    }
  }, [smoothScrollRef]);

  const unLock = useCallback(() => {
    if (hasGsapScrollSmootherRef.current) {
      scrollSmootherRef?.current?.paused(false);
    } else if (hasSmoothScroll.current) {
      smoothScrollRef?.current?.updatePluginOptions('lock', { isLock: false });
    } else {
      lockPosition.current = undefined;
    }
  }, [smoothScrollRef]);

  const scaleDelta = useCallback(
    (speed: number = 0.25) => {
      if (hasGsapScrollSmootherRef.current) {
        //
      } else if (hasSmoothScroll.current) {
        smoothScrollRef?.current?.setMomentum(0, 0);
        smoothScrollRef?.current?.updatePluginOptions('scaleDelta', { speed, isScaling: true });
      }
    },
    [smoothScrollRef],
  );

  const resetScaleDelta = useCallback(() => {
    if (hasGsapScrollSmootherRef.current) {
      //
    } else if (hasSmoothScroll.current) {
      smoothScrollRef?.current?.updatePluginOptions('scaleDelta', { isScaling: false });
    }
  }, [smoothScrollRef]);

  const isAndroid = () => /Android/i.test(navigator.userAgent);

  const scrollIntoView = useCallback(
    (element: HTMLElement, options?: Partial<ScrollIntoViewOptions>) => {
      if (isAndroid()) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
      if (hasGsapScrollSmootherRef.current) {
        smoothScrollRef?.current?.scrollIntoView(element, options);
      } else if (hasSmoothScroll.current) {
        smoothScrollRef?.current?.scrollIntoView(element, options);
      } else {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    },
    [smoothScrollRef],
  );

  const onScroll = useCallback(
    (status?: ScrollStatus) => {
      // ScrollSmoother (GSAP)
      if (hasGsapScrollSmootherRef.current && scrollSmootherRef.current) {
        // document.body.scrollHeight,
        // document.body.scrollWidth,
        scrollStatusRef.current = {
          offset: {
            x: 0,
            y: scrollSmootherRef.current?.scrollTop() as number,
          },
          limit: {
            x: 0,
            y: 0,
          },
        };
        // SmoothScroll (https://idiotwu.github.io/smooth-scrollbar/)
      } else if (hasSmoothScroll.current) {
        scrollStatusRef.current = status as ScrollStatus;
        // Native Scroll
      } else {
        scrollStatusRef.current = {
          offset: {
            x: window.scrollX || document.documentElement.scrollLeft,
            y: window.scrollY || document.documentElement.scrollTop,
          },
          limit: {
            x: document.body.scrollWidth,
            y: document.body.scrollHeight,
          },
        };
      }

      if (onScrollUpdate) onScrollUpdate(scrollStatusRef.current);
    },
    [scrollStatusRef, onScrollUpdate, hasSmoothScroll],
  );

  const scrollTo = useCallback(
    (
      x: number,
      y: number,
      duration: number,
      options: ScrollToOptions & { target?: gsap.DOMTarget },
    ) => {
      // ScrollSmoother (GSAP)
      if (hasGsapScrollSmootherRef.current && scrollSmootherRef.current) {
        gsap.to(scrollSmootherRef?.current, {
          scrollTop: scrollSmootherRef?.current?.offset(
            options?.target || scrollContentRef.current,
            `${y}`,
          ),
          duration: duration / 1000,
          ease: options?.easing || 'Quad.easeInOut',
          onUpdate: () => {
            onScroll();
          },
          onComplete: () => {
            if (options?.callback) {
              // @ts-ignore
              options.callback();
            }
          },
        });
        // SmoothScroll (https://idiotwu.github.io/smooth-scrollbar/)
      } else if (hasSmoothScroll.current) {
        smoothScrollRef?.current?.scrollTo(x, y, duration, options);
        // Native Scroll
      } else {
        gsap.to(window, {
          duration: duration / 1000,
          ease: options?.easing || 'Quad.easeInOut',
          scrollTo: {
            y,
            x,
          },
          onComplete: () => {
            if (options?.callback) {
              // @ts-ignore
              options.callback();
            }
          },
        });
      }
    },
    [smoothScrollRef, hasGsapScrollSmootherRef, onScroll],
  );

  useEffect(() => {
    if (lockScroll) {
      lock();
    } else {
      unLock();
    }
  }, [lockScroll, lock, unLock]);

  useIsomorphicLayoutEffect(() => {
    if (scrollWrapperRef.current) {
      // Attach scroll listener
      scrollHandlerRef.current = onScroll;

      // ScrollSmoother (GSAP)
      if (hasGsapScrollSmootherRef.current && !scrollSmootherRef.current) {
        // create the scrollSmoother before your scrollTriggers
        scrollSmootherRef.current = ScrollSmoother.create({
          wrapper: scrollWrapperRef.current,
          content: scrollContentRef.current,
          onUpdate: () => {
            onScroll();
          },
          normalizeScroll: true,
          ignoreMobileResize: true,
          smooth: 0.8, // how long (in seconds) it takes to "catch up" to the native scroll position
          effects: false, // looks for data-speed and data-lag attributes on elements
          smoothTouch: false, // much shorter smoothing time on touch devices (default is NO smoothing on touch devices)
        });
        // SmoothScroll (https://idiotwu.github.io/smooth-scrollbar/)
      } else if (hasSmoothScroll.current) {
        smoothScrollRef.current = Scrollbar.init(scrollWrapperRef.current, {
          ...{
            renderByPixels: !isTouch,
            damping: isTouch ? 0.05 : 0.1,
            delegateTo: document,
            // plugins: { overscroll: { maxOverscroll: 50, damping: 0.1 } },
          },
          ...props.scrollBarOptions,
        });

        ScrollTrigger.scrollerProxy(scrollWrapperRef.current, {
          scrollTop: (value: number | undefined) => {
            if (arguments.length > 0 && smoothScrollRef.current && typeof value !== 'undefined') {
              smoothScrollRef.current.scrollTop = value;
            }
            return smoothScrollRef.current?.scrollTop || 0;
          },
          getBoundingClientRect: () => {
            const { width, height, top, left } =
              scrollWrapperRef.current?.getBoundingClientRect() || {
                width: 0,
                height: 0,
                top: 0,
                left: 0,
              };

            return {
              top,
              left,
              width,
              height,
            };
          },
        });

        smoothScrollRef.current.addListener(ScrollTrigger.update);
      }

      // ScrollSmoother GSAP
      if (hasGsapScrollSmootherRef.current) {
        // onUpdate method is attached in the constructor instead.
        // SmoothScroll (https://idiotwu.github.io/smooth-scrollbar/)
      } else if (hasSmoothScroll.current) {
        smoothScrollRef.current?.addListener(scrollHandlerRef.current);
        // Native Scroll
      } else {
        scrollListener = addEventListener(
          window,
          'scroll',
          scrollHandlerRef.current as unknown as (event: Event) => void,
        );
        const wheelEvent = 'onwheel' in document.createElement('div') ? 'wheel' : 'mousewheel';
        wheelListener = addEventListener(
          window,
          wheelEvent,
          (event: Event) => {
            if (lockPosition.current !== undefined) {
              event.preventDefault();
            }
          },
          { passive: false },
        );

        touchListener = addEventListener(
          window,
          'touchmove',
          (event) => {
            if (lockPosition.current !== undefined) {
              event.preventDefault();
            }
          },
          { passive: false },
        );
      }
    }

    return () => {
      if (touchListener) {
        touchListener();
      }

      if (wheelListener) {
        wheelListener();
      }

      if (scrollListener) {
        scrollListener();
      }

      if (scrollSmootherRef.current) {
        scrollSmootherRef.current?.kill();
      }

      smoothScrollRef.current?.removeListener(scrollHandlerRef.current as ScrollListener);
      smoothScrollRef.current?.destroy();
    };
  }, []);

  return (
    <ScrollProviderContext.Provider
      value={{
        unLock,
        lock,
        scaleDelta,
        resetScaleDelta,
        scrollIntoView,
        scrollTo,
        scrollStatusRef,
        scrollWrapperRef,
        hasSmoothScroll,
        hasGsapScrollSmootherRef,
      }}
    >
      <div
        ref={scrollWrapperRef}
        className={classNames(styles.scrollBar, 'scroll-wrapper', props.wrapperClassName)}
      >
        <div ref={scrollContentRef} className={classNames('scroll-inner-content')}>
          {props.children}
        </div>
      </div>
    </ScrollProviderContext.Provider>
  );
}
